'use client';
import { gql } from '@apollo/client';
import { useMediaQuery } from '@mnd-frontend/hooks';
import { Button, Icons } from '@mnd-frontend/ui/publish/components';
import { useLocation } from '@mnd-frontend/ui/publish/contexts';
import { media } from '@mnd-frontend/ui/publish/utils';
import { animated, useSpring } from '@react-spring/web';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useReportStaticData, useReportTranslation } from '../../hooks';
import { getDefaultSectionTitle } from '../../utils';
import InfoModal from '../common/InfoModal';

const Wrapper = styled.div`
  padding-top: 6rem;
`;

const MenuWrapper = styled(animated.div)`
  display: flex;
  justify-content: space-between;
  position: fixed;
  z-index: 10;
  top: 0;
  left: 0;
  width: calc(100% - 2rem);
  background: white;
  border-radius: 3.125rem;
  padding: 0.5rem 2rem;
  margin: 1rem;
  box-shadow: 0px 2px 4px 0px rgba(49, 73, 164, 0.1);
`;

const NavigationWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  text-align: center;

  a {
    color: var(--mnd-default-text-color);

    &:hover {
      opacity: 0.8;
    }
  }
`;

const NavigationButtonsWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MobileMenuWrapper = styled.div`
  position: relative;
`;

const MobileMenu = styled.div<{ $isVisible: boolean }>`
  display: ${({ $isVisible }) => ($isVisible ? 'block' : 'none')};
  position: fixed;
  padding: 0 1rem 1rem 1rem;
  background: white;
  transition: left 0.3s ease-in-out;
  z-index: 1000;
  text-align: left;
  border-radius: 1rem;

  div {
    margin-top: 1rem;
    margin-bottom: 1rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

type Props = {
  backHref?: string;
  copyPublicReportLink?: () => void;
  settingsSection?: React.ReactNode;
  downloadSection?: React.ReactNode;
  actionsDisabled?: boolean;
};

export const REPORT2_MENU_SECTION_FRAGMENT = gql`
  fragment Report2MenuSection on ReportMenuSection {
    id
    sections {
      id
      title
    }
  }
`;

export const ReportHeader = ({
  backHref,
  copyPublicReportLink,
  settingsSection,
  downloadSection,
  actionsDisabled,
}: Props) => {
  const { menuItems } = useReportStaticData();
  const { t } = useReportTranslation();
  const [infoModal, setInfoModal] = useState(false);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const isMobile = useMediaQuery(media.lessThan('navigationBreakpoint'));
  const location = useLocation();

  const [isVisible, setIsVisible] = useState(true);
  const [menuHeight, setMenuHeight] = useState(0);
  const menuRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const lastScrollTopRef = useRef(0);
  const scrollUpDistanceRef = useRef(0);
  const scrollTimeoutRef = useRef<number | null>(null);
  const [threshold, setThreshold] = useState(0);

  const handleScroll = useCallback(() => {
    const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
    const deltaScroll = currentScroll - lastScrollTopRef.current;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = window.setTimeout(() => {
      scrollUpDistanceRef.current = 0;
    }, 500);

    if (currentScroll <= 0) {
      setIsVisible(true);
      scrollUpDistanceRef.current = 0;
    } else if (currentScroll <= threshold) {
      setIsVisible(true);
    } else {
      if (deltaScroll > 0) {
        scrollUpDistanceRef.current = 0;
        if (isVisible) {
          setIsVisible(false);
        }
      } else if (deltaScroll < 0) {
        scrollUpDistanceRef.current += -deltaScroll;
        if (scrollUpDistanceRef.current >= 200) {
          if (!isVisible) {
            setIsVisible(true);
          }
        }
      }
    }

    lastScrollTopRef.current = currentScroll;
  }, [isVisible, threshold]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ?
  useEffect(() => {
    if (menuRef.current) {
      const { height, marginTop } = window.getComputedStyle(menuRef.current);
      setMenuHeight(Number.parseFloat(height) + Number.parseFloat(marginTop));
    }

    const updateThreshold = () => {
      if (menuRef.current && wrapperRef.current) {
        const menuRect = menuRef.current.getBoundingClientRect();
        const wrapperRect = wrapperRef.current.getBoundingClientRect();
        const calculatedThreshold =
          wrapperRef.current.offsetTop +
          wrapperRect.height -
          menuRect.height -
          Number.parseFloat(window.getComputedStyle(menuRef.current).marginTop);
        setThreshold(calculatedThreshold);
      }
    };

    updateThreshold();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateThreshold);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateThreshold);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, isVisible]);

  const springProps = useSpring({
    to: { top: isVisible ? '0px' : `-${menuHeight}px` },
    config: { tension: 220, friction: 20 },
  });

  const scrollTo = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offsetTop = element.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  return (
    <>
      <Wrapper ref={wrapperRef}>
        <MenuWrapper ref={menuRef} style={springProps}>
          <NavigationWrapper>
            {isMobile ? (
              <MobileMenuWrapper>
                <Button
                  type="button"
                  configuration="text"
                  aria-label={t('reports_menu_toggle')}
                  onClick={() => setMobileSidebar(!mobileSidebar)}
                  icon={<Icons.Menu />}
                />
                <MobileMenu $isVisible={mobileSidebar}>
                  {backHref && (
                    <Button
                      href={backHref}
                      configuration="text"
                      leadingIcon={<Icons.ChevronLeft />}
                    >
                      {t('common_back')}
                    </Button>
                  )}
                  {menuItems.map(section => (
                    <div key={`menu-item-${section.id}`}>
                      <a
                        href={`${location.pathname}#report_section_${section.id}`}
                        onClick={e => {
                          e.preventDefault();
                          scrollTo(`${section.id}`);
                          setMobileSidebar(false);
                        }}
                      >
                        {section.title || getDefaultSectionTitle({ section, t })}
                      </a>
                    </div>
                  ))}
                </MobileMenu>
              </MobileMenuWrapper>
            ) : (
              <>
                {backHref && (
                  <Button href={backHref} configuration="text" leadingIcon={<Icons.ChevronLeft />}>
                    {t('common_back')}
                  </Button>
                )}
                {menuItems.map(section => (
                  <a
                    key={`menu-item-${section.id}`}
                    href={`${location.pathname}#report_section_${section.id}`}
                    onClick={e => {
                      e.preventDefault();
                      scrollTo(`${section.id}`);
                    }}
                  >
                    {section.title || getDefaultSectionTitle({ section, t })}
                  </a>
                ))}
              </>
            )}
          </NavigationWrapper>
          <NavigationButtonsWrapper>
            {copyPublicReportLink && (
              <Button
                type="button"
                configuration="toned"
                onClick={copyPublicReportLink}
                aria-label={t('reports_share-modal-copy-link')}
                icon={<Icons.Link />}
                disabled={actionsDisabled}
              />
            )}
            {downloadSection}
            <Button
              type="button"
              configuration="toned"
              onClick={() => setInfoModal(true)}
              aria-label={t('reports_show_help')}
              icon={<Icons.CircleQuestion />}
            />
            {settingsSection}
          </NavigationButtonsWrapper>
        </MenuWrapper>
      </Wrapper>
      {infoModal && <InfoModal close={() => setInfoModal(false)} />}
    </>
  );
};
